import "../Common/css/styles.css";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Button,
    TextField,
    Typography,
    Modal,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState, ChangeEvent, useEffect, useRef } from "react";
import { Send } from "@mui/icons-material";
import { chatApi, chatSignalR } from "src/Services/chat_api";
import env from "src/env";

interface StudentInstructorChatProps {
    open: boolean;
    handleClose: () => void;
    instructorId?: string;
    instructorName?: string;
    isChatStarted?: boolean;
    conversationId?: number;
}

interface AttachmentView {
    fileUrl: string;
    fileName: string;
    type: "image" | "pdf";
}

export default function StudentInstructorChat({
    open,
    handleClose,
    instructorId,
    instructorName,
    isChatStarted,
    conversationId,
}: StudentInstructorChatProps) {
    const [message, setMessage] = useState<string>("");
    const [attachments, setAttachments] = useState<File[]>([]);
    const [messages, setMessages] = useState<any[]>([]);
    const [viewAttachment, setViewAttachment] = useState<AttachmentView | null>(null);
    const [localConversationId, setLocalConversationId] = useState<number | null>(conversationId || null);
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [isSending, setIsSending] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const localConversationIdRef = useRef<number | null>(conversationId || null);

    const userId = sessionStorage.getItem("userId") || "";
    const BASE_URL = env.BASE_URL;

    // Keep ref in sync with state
    useEffect(() => {
        localConversationIdRef.current = localConversationId;
    }, [localConversationId]);

    // Connect to SignalR when dialog opens
    useEffect(() => {
        if (open) {
            chatSignalR.connect()
                .then(() => {
                    setIsConnected(true);
                    //console.log('SignalR connected for student chat');
                })
                .catch(err => {
                    console.error('Failed to connect SignalR:', err);
                    setIsConnected(false);
                });
        }

        return () => {
            // Cleanup: disconnect when dialog closes
            if (!open) {
                chatSignalR.disconnect().catch(console.error);
                setIsConnected(false);
            }
        };
    }, [open]);

    // Polling for new messages (temporary workaround until backend broadcasts via SignalR)
    useEffect(() => {
        if (!open || !localConversationId) return;

        const pollMessages = async () => {
            try {
                const res = await chatApi.getMessages(localConversationId, Number(userId), 1, 50);
                setMessages(prev => {
                    // Only update if there are new messages
                    if (res.length > prev.length) {
                        return res;
                    }
                    // Check if last message is different
                    if (res.length > 0 && prev.length > 0) {
                        const lastNew = res[res.length - 1];
                        const lastOld = prev[prev.length - 1];
                        if (lastNew.id !== lastOld.id) {
                            return res;
                        }
                    }
                    return prev;
                });
            } catch (error) {
                console.error('Polling error:', error);
            }
        };

        // Poll every 3 seconds
        const intervalId = setInterval(pollMessages, 3000);

        return () => clearInterval(intervalId);
    }, [open, localConversationId, userId]);

    // Subscribe to SignalR events
    useEffect(() => {
        if (!isConnected) return;

        // Handle incoming real-time messages
        const handleReceiveMessage = (data: any) => {
            //console.log('SignalR ReceiveMessage:', data);

            // Only add message if it belongs to current conversation
            if (data.conversationId === localConversationIdRef.current) {
                setMessages(prev => {
                    // Avoid duplicates
                    const exists = prev.some(m => m.id === data.id);
                    if (exists) return prev;
                    return [...prev, data];
                });

                // Mark as read if the message is from the other party
                if (data.senderType !== 1 && localConversationIdRef.current) {
                    chatApi.markAsRead(localConversationIdRef.current, Number(userId), 1).catch(console.error);
                }
            }
        };

        // Handle typing indicator
        const handleUserTyping = (data: any) => {
            if (data.conversationId === localConversationIdRef.current && data.senderType !== 1) {
                setIsTyping(true);
            }
        };

        const handleStopTyping = (data: any) => {
            if (data.conversationId === localConversationIdRef.current) {
                setIsTyping(false);
            }
        };

        chatSignalR.on('ReceiveMessage', handleReceiveMessage);
        chatSignalR.on('UserTyping', handleUserTyping);
        chatSignalR.on('StopTyping', handleStopTyping);

        return () => {
            chatSignalR.off('ReceiveMessage', handleReceiveMessage);
            chatSignalR.off('UserTyping', handleUserTyping);
            chatSignalR.off('StopTyping', handleStopTyping);
        };
    }, [isConnected, userId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        setLocalConversationId(conversationId || null);
    }, [conversationId]);


    const handleGetMessages = async (id: number) => {
        setMessages([]); // Clear previous messages immediately
        if (!id) return;

        try {
            const res = await chatApi.getMessages(id, Number(userId), 1, 50);
            setMessages(res);
            // Mark messages as read when loading conversation
            chatApi.markAsRead(id, Number(userId), 1).catch(console.error);
        } catch (error) {
            setMessages([]);
        }
    };

    // Handle typing indicator - send to SignalR
    const handleTyping = () => {
        if (localConversationId && isConnected) {
            chatSignalR.userTyping(Number(userId), localConversationId, 'Student').catch(console.error);

            // Clear previous timeout
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            // Stop typing after 2 seconds of inactivity
            typingTimeoutRef.current = setTimeout(() => {
                if (localConversationId) {
                    chatSignalR.stopTyping(Number(userId), localConversationId).catch(console.error);
                }
            }, 2000);
        }
    };


    const handleAttach = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const file = e.target.files[0];

        if (!["image/jpeg", "image/png", "image/jpg", "application/pdf"].includes(file.type)) {
            alert("Only images or PDF files are allowed.");
            e.target.value = "";
            return;
        }

        if (attachments.length >= 1) {
            alert("Only one attachment is allowed. Remove the existing file first.");
            e.target.value = "";
            return;
        }

        setAttachments([file]);
        e.target.value = "";
    };

    const handleRemoveAttachment = (index: number) => {
        setAttachments(attachments.filter((_, i) => i !== index));
    };

    const handleSend = async () => {
        if (isSending) return;
        if (message.trim() === "" && attachments.length === 0) return;

        // Stop typing indicator
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        if (localConversationId && isConnected) {
            chatSignalR.stopTyping(Number(userId), localConversationId).catch(console.error);
        }

        if (!localConversationId) {

            if (attachments.length > 0) {
                alert("Cannot send attachment in the first message. Please send a text message first.");
                return;
            }

            try {
                setIsSending(true);
                const res = await chatApi.startConversation(Number(userId), Number(instructorId), message);
                const newId = Number(res.message);
                setLocalConversationId(newId);
                setMessage("");
                handleGetMessages(newId);
            } catch (error) {
                console.error("Error starting conversation:", error);
            } finally {
                setIsSending(false);
            }
            return;
        }

        try {
            setIsSending(true);
            const sentMessage = await chatApi.sendMessage({
                conversationId: localConversationId,
                senderId: Number(userId),
                senderType: 1,
                content: message,
                attachment: attachments.length > 0 ? attachments[0] : null,
            });

            const messageContent = message; // Store before clearing
            setMessage("");
            setAttachments([]);

            // Create the message object for display
            const messageObj = sentMessage?.id ? {
                ...sentMessage,
                createdAt: sentMessage.createdAt || new Date().toISOString()
            } : {
                id: Date.now(),
                conversationId: localConversationId,
                senderId: Number(userId),
                senderType: 1,
                content: messageContent,
                createdAt: new Date().toISOString(),
                attachments: []
            };

            // Add sent message to local state
            setMessages(prev => {
                const exists = prev.some(m => m.id === messageObj.id);
                if (exists) return prev;
                return [...prev, messageObj];
            });

            // Note: Backend should broadcast ReceiveMessage after saving
            // Until then, polling will fetch new messages for the receiver
        } catch (error) {
            console.error("Send message error", error);
        } finally {
            setIsSending(false);
        }
    };


    const handleAttachmentClick = (file: any) => {
        const isImage = file.fileType.startsWith("image/");
        const isPDF = file.fileType === "application/pdf";
        const fileUrl = `${BASE_URL}${file.filePath}`;

        if (isImage) setViewAttachment({ fileUrl, fileName: file.fileName, type: "image" });
        if (isPDF) setViewAttachment({ fileUrl, fileName: file.fileName, type: "pdf" });
    };

    const handleCloseView = () => setViewAttachment(null);

    useEffect(() => {
        if (localConversationId) {
            handleGetMessages(localConversationId);
        } else {
            setMessages([]); // Clear if no conversation selected
        }
    }, [localConversationId]);



    return (
        <>
            <Dialog
                fullScreen
                open={open}
                onClose={handleClose}
                PaperProps={{ sx: { bgcolor: "#000000ff", color: "#fff" } }}
            >
                {/* Header */}
                <Box className="chat-header">
                    <DialogTitle className="chat-title">Chat with {instructorName}</DialogTitle>
                    <IconButton edge="end" color="inherit" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Chat Body */}
                <DialogContent className="chat-body">

                    {messages.map((msg, index) => {
                        const isOutgoing = msg.senderType === 1;

                        return (
                            <div key={index} className={`chat-message ${isOutgoing ? "outgoing" : "incoming"}`}>
                                {msg.content && <span>{msg.content}</span>}

                                {msg.attachments && msg.attachments.length > 0 && (
                                    <div className="chat-attachment-container">
                                        {msg.attachments.map((file: any, idx: number) => {
                                            const isImage = file.fileType.startsWith("image/");
                                            const isPDF = file.fileType === "application/pdf";
                                            const fileUrl = `${BASE_URL}${file.filePath}`;

                                            return (
                                                <div
                                                    key={idx}
                                                    className="chat-attachment-item"
                                                    onClick={() => handleAttachmentClick(file)}
                                                >
                                                    {isImage && <img src={fileUrl} className="chat-img-thumb" alt={file.fileName} />}
                                                    {isPDF && (
                                                        <div className="chat-pdf-thumb">
                                                            <AttachFileIcon fontSize="small" />
                                                            <p>{file.fileName}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="chat-time">
                                    {msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ''}
                                </div>
                            </div>
                        );
                    })}

                    {/* Typing Indicator */}
                    {isTyping && (
                        <div className="chat-message incoming">
                            <span style={{ fontStyle: 'italic', color: '#888' }}>
                                {instructorName} is typing...
                            </span>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </DialogContent>

                {/* Attachments Preview */}
                {attachments.length > 0 && (
                    <Box className="chat-attachments">
                        {attachments.map((file, idx) => {
                            const isImage = file.type.startsWith("image/");
                            const isPDF = file.type === "application/pdf";

                            return (
                                <Box key={idx} className="chat-attachment-preview">
                                    <Box className="chat-attachment-clickable" onClick={() => handleAttachmentClick(file)}>
                                        {isImage && <img src={URL.createObjectURL(file)} alt={file.name} className="chat-attachment-image" />}
                                        {isPDF && (
                                            <Box className="chat-attachment-doc">
                                                <AttachFileIcon fontSize="small" />
                                                <Typography className="chat-attachment-doc-name">{file.name}</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    <IconButton
                                        size="small"
                                        className="chat-attachment-delete"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveAttachment(idx);
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* Input Bar */}
                <Box className="chat-input-bar">
                    <input
                        accept="image/*,.pdf"
                        id="attach-file"
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleAttach}
                    />

                    <label htmlFor="attach-file" className="attach-label">
                        <AttachFileIcon />
                    </label>

                    <TextField
                        className="chat-input-field"
                        placeholder="Type your message..."
                        variant="outlined"
                        size="small"
                        multiline
                        value={message}
                        onChange={(e) => {
                            setMessage(e.target.value);
                            handleTyping();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                        fullWidth
                    />

                    <Button className="chat-send-btn" onClick={handleSend} disabled={isSending}>
                        <Send sx={{ fontSize: 28, color: "#d2c919" }} />
                    </Button>
                </Box>
            </Dialog>

            {/* Attachment Viewer Modal */}
            <Modal
                open={!!viewAttachment}
                onClose={handleCloseView}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(0,0,0,0.9)",
                }}
            >
                {viewAttachment ? (
                    <Box
                        sx={{
                            position: "relative",
                            width: "100vw",
                            height: "100vh",
                            bgcolor: "#000",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                        }}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                px: 3,
                                py: 2,
                                borderBottom: "1px solid #333",
                            }}
                        >
                            <div></div>
                            <IconButton sx={{ color: "#fff" }} onClick={handleCloseView}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Content */}
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 2,
                            }}
                        >
                            {viewAttachment.type === "image" ? (
                                <img
                                    src={viewAttachment.fileUrl}
                                    alt={viewAttachment.fileName}
                                    style={{
                                        maxWidth: "350px",
                                        height: "auto",
                                        objectFit: "contain",
                                        borderRadius: 0,
                                    }}
                                />
                            ) : (
                                <iframe
                                    src={viewAttachment.fileUrl}
                                    title={viewAttachment.fileName}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        minHeight: "100%",
                                        border: "none",
                                        borderRadius: 0,
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                ) : (
                    <></>
                )}
            </Modal>


        </>
    );
}
